import AutoCompleteSearch, {
  AutoCompleteSearchRef,
} from "@/components/common/inputs/AutoCompleteSearch";
import { AppThunkDispatch } from "@/store";
import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLiveChatState } from "./Provider/LiveChatStateProvider";
import actionsHandler from "@/store/actions/actionHandler";
import { ChatScreenType } from "./types/chat";
import { CreateGroupResponse } from "./types/api-response-types";
import { toast } from "react-toastify";

interface UserSearch {
  id: number;
  name: string;
  company: string;
  role: string;
  tier: string;
  company_id: string;
  room_id?: string;
}

const UserSearch = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearch | null>(null);
  const {
    handleScreenChange,
    handleCreatingIndividualChatRoom,
    isIndividualChatCreationLoading,
  } = useLiveChatState();
  const autoCompleteSearchRef = useRef<AutoCompleteSearchRef>(null);
  const handleSearch = useCallback(
    async (query: string): Promise<UserSearch[]> => {
      if (!query || query.length < 1) return [];

      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("searchUsers");
        const response = await dispatch(
          authenticatedCall({
            customQuery: `search=${query}`,
            method: "get",
          }),
        );

        return response.ok ? response.data.data : [];
      } catch (error) {
        console.error("Error searching users:", error);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  const handleUserSelect = useCallback(
    (user: unknown): void => {
      const selectedUser = user as UserSearch;
      setSelectedUser(selectedUser);
      console.log("selectedUser: ", selectedUser);
      if (!selectedUser) {
        return;
      } else if (!selectedUser.company_id) {
        toast.warning("User does not have a company");
        return;
      }
      if (!selectedUser.room_id) {
        handleCreatingIndividualChatRoom({
          id: selectedUser.id,
          name: selectedUser.name,
          room_id: null,
        }).then((response: CreateGroupResponse) => {
          handleScreenChange({
            type: ChatScreenType.USER,
            data: {
              user: { ...selectedUser, room_id: response.room_id.toString() },
              companyId: selectedUser.company_id,
            },
          });
          handleClosingSearch();
        });
      } else {
        handleScreenChange({
          type: ChatScreenType.USER,
          data: {
            user: { ...selectedUser, room_id: selectedUser.room_id.toString() },
            companyId: selectedUser.company_id,
          },
        });
        handleClosingSearch();
      }

      // Handle user selection
    },
    [handleCreatingIndividualChatRoom, handleScreenChange, setSelectedUser],
  );

  const handleClosingSearch = () => {
    autoCompleteSearchRef.current?.handleClose();
    autoCompleteSearchRef.current?.handleResetSearch();
    setSelectedUser(null);
  };

  return (
    <AutoCompleteSearch
      ref={autoCompleteSearchRef}
      isSearchedRowLoading={
        isIndividualChatCreationLoading[selectedUser?.id ?? ""]
      }
      onSearch={handleSearch}
      onSelect={handleUserSelect}
      isLoading={isLoading}
      placeholder="Search for user"
      noResultsMessage="No users found"
    />
  );
};

export default UserSearch;
